import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowUpRight } from "lucide-react";
import { getAppMedia, mediaUrl } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const _labelSlug = (s) =>
  (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "feature";

const FeatureSpotlight = ({ app }) => {
  const { t, lang } = useLang();
  const [media, setMedia] = useState([]);

  useEffect(() => {
    if (!app?.slug) return;
    getAppMedia(app.slug).then(setMedia).catch(() => setMedia([]));
  }, [app?.slug]);

  // Pick first item per unique feature_label that has both a label and an image, max 6.
  const items = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const m of media) {
      const label = (m.feature_label || "").trim();
      if (!label) continue;
      if (m.type !== "image") continue; // spotlight strip works with images
      if (seen.has(label.toLowerCase())) continue;
      seen.add(label.toLowerCase());
      out.push(m);
      if (out.length === 6) break;
    }
    return out;
  }, [media]);

  // SEO: ImageObject JSON-LD
  useEffect(() => {
    if (items.length === 0) return;
    const id = "spotlight-jsonld";
    document.getElementById(id)?.remove();
    const ld = items.map((m) => ({
      "@type": "ImageObject",
      contentUrl: mediaUrl(m.url),
      caption: `${app.name} — ${m.feature_label}${m.caption ? `: ${m.caption}` : ""}`,
      name: `${app.name} ${m.feature_label}`,
      representativeOfPage: false,
    }));
    const script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": ld,
    });
    document.head.appendChild(script);
    return () => script.remove();
  }, [items, app?.name]);

  if (items.length === 0) return null;

  const isNl = lang === "nl";
  const title = isNl
    ? `Feature-spotlight: dit doet ${app.name} echt`
    : `Feature spotlight: what ${app.name} actually does`;
  const subtitle = isNl
    ? `Zes kernfuncties van ${app.name}, elk uitgelicht met een screenshot uit de app — geen marketingpraat, alleen wat je daadwerkelijk te zien krijgt.`
    : `Six core features of ${app.name}, each illustrated with a screenshot from the live product — no marketing fluff, just what you actually see inside.`;

  return (
    <section
      className="bg-[#F8FAFC] border-y border-[#E2E8F0]"
      data-testid="feature-spotlight"
    >
      <div className="hac-container py-16 md:py-20">
        <div className="flex items-start gap-3 mb-3">
          <Sparkles size={18} className="text-[#2563EB] mt-1 shrink-0" />
          <div>
            <div className="overline text-[#2563EB] mb-2">
              {isNl ? "Voor & na — visueel bewijs" : "Side-by-side — visual proof"}
            </div>
            <h2 className="font-heading text-3xl md:text-4xl text-[#0F172A] leading-tight max-w-3xl">
              {title}
            </h2>
            <p className="text-[#64748B] mt-3 max-w-3xl">{subtitle}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {items.map((m, idx) => (
            <Link
              key={m.id}
              to={`/apps/${app.slug}/feature/${_labelSlug(m.feature_label)}`}
              className="group bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden hover:border-[#2563EB] transition-colors block"
              data-testid={`spotlight-card-${m.id}`}
            >
              <div className="relative aspect-[4/3] bg-[#F1F5F9] overflow-hidden">
                <img
                  src={mediaUrl(m.url)}
                  alt={`${app.name} — ${m.feature_label}${m.caption ? `, ${m.caption}` : ""}`}
                  className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
                  loading="lazy"
                />
                <span className="absolute top-3 left-3 bg-white text-[#0F172A] text-[10px] uppercase tracking-[0.18em] font-bold px-2.5 py-1 rounded-full border border-[#E2E8F0]">
                  {String(idx + 1).padStart(2, "0")} · {m.feature_label}
                </span>
                <span className="absolute top-3 right-3 bg-[#2563EB] text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowUpRight size={12} />
                </span>
              </div>
              <div className="p-5">
                <h3 className="font-bold text-[#0F172A] mb-1 leading-snug">
                  {m.caption || `${app.name} ${m.feature_label}`}
                </h3>
                <p className="text-sm text-[#64748B] leading-relaxed">
                  {isNl
                    ? `Hoe ${app.name} ${m.feature_label.toLowerCase()} aanpakt — direct uit de app.`
                    : `How ${app.name} handles ${m.feature_label.toLowerCase()} — straight from the app.`}
                </p>
              </div>
            </Link>
          ))}
        </div>

        {/* Advertiser hint — visible only when there's room for more spotlight slots */}
        {items.length < 6 && (
          <div
            className="mt-10 bg-gradient-to-r from-[#EFF6FF] to-white border border-[#DBEAFE] rounded-2xl p-5 flex items-start gap-4"
            data-testid="spotlight-advertiser-hint"
          >
            <Sparkles size={18} className="text-[#2563EB] mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-bold text-[#0F172A]">
                {isNl
                  ? `Bent u ${app.name}? Laat ${6 - items.length} extra feature(s) visueel uitlichten.`
                  : `Are you ${app.name}? Showcase ${6 - items.length} more feature${6 - items.length === 1 ? "" : "s"} here.`}
              </div>
              <p className="text-sm text-[#64748B] mt-1">
                {isNl
                  ? "Adverteerders met een geverifieerd profiel krijgen tot 6 spotlight-kaarten met JSON-LD beeldschema — direct vindbaar via Google Images."
                  : "Verified-profile advertisers get up to 6 spotlight cards with JSON-LD image schema — discoverable via Google Images."}
              </p>
            </div>
            <a
              href="/sponsor"
              className="hac-btn-primary shrink-0"
              data-testid="spotlight-cta-sponsor"
            >
              {isNl ? "Claim profiel" : "Claim profile"} <ArrowUpRight size={14} />
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

export default FeatureSpotlight;
